import type { WishlistParticipantScope } from '#src/pages/group/own_wishlist_card/use_get_own_wishlist.ts';
import { getWishlistQueryKey } from '#src/pages/group/own_wishlist_card/use_get_own_wishlist.ts';
import type { WishlistClient } from '#src/wishlists/http/wishlist_client.ts';
import type {
  GetWishlistResponse,
  PerformWishlistActionErrorResponse,
  PerformWishlistActionResponse,
  WishlistActionRequest,
} from '@packages/contracts/wishlist';
import { WishlistAction, WishlistErrorCodes } from '@packages/contracts/wishlist';
import { exists } from '@packages/primitives/exists';
import { UnreachableError } from '@packages/primitives/unreachable_error';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export function useWishlistAction(
  wishlistClient: WishlistClient,
  { groupId, participantId }: WishlistParticipantScope,
) {
  const queryClient = useQueryClient();

  const mutation = useMutation<PerformWishlistActionResponse, Error, WishlistActionRequest>({
    mutationFn: (action: WishlistActionRequest) =>
      wishlistClient.performAction({ groupId, participantId, action }),
    onSuccess: result => {
      if (!result.success) {
        return;
      }

      queryClient.setQueryData<GetWishlistResponse | undefined>(
        getWishlistQueryKey({ groupId, participantId }),
        current => patchWishlistResult(current, result),
      );
    },
  });

  const { data: result, error: httpError, ...rest } = mutation;

  return {
    ...rest,
    result,
    httpError,
    displayErrorMessage: getDisplayErrorMessage(result, httpError),
  };
}

const GENERIC_ERROR_MESSAGE = 'We could not save your wishlist changes. Please try again.';

const ERROR_MESSAGES: Record<PerformWishlistActionErrorResponse['code'], string> = {
  [WishlistErrorCodes.INVALID_INPUT]: 'Please review the item details and try again.',
  [WishlistErrorCodes.LIMIT_REACHED]: 'You can add up to 25 wishlist items.',
  [WishlistErrorCodes.NOT_FOUND]: 'That wishlist item no longer exists.',
};

function patchWishlistResult(
  current: GetWishlistResponse | undefined,
  result: Extract<PerformWishlistActionResponse, { success: true }>,
): GetWishlistResponse | undefined {
  if (!exists(current) || !current.success) {
    return current;
  }

  switch (result.action) {
    case WishlistAction.CREATE:
      return {
        ...current,
        items: [...current.items, result.item],
      };

    case WishlistAction.SET:
      return {
        ...current,
        items: current.items.map(item => (item.id === result.item.id ? result.item : item)),
      };

    case WishlistAction.DELETE:
      return {
        ...current,
        items: current.items.filter(item => item.id !== result.id),
      };

    default:
      throw new UnreachableError(result);
  }
}

function getDisplayErrorMessage(
  result: PerformWishlistActionResponse | undefined,
  unexpectedError: Error | null,
) {
  if (unexpectedError) {
    return GENERIC_ERROR_MESSAGE;
  }

  if (result && !result.success) {
    return ERROR_MESSAGES[result.code] ?? GENERIC_ERROR_MESSAGE;
  }
}
